/**
 * CloudAlertsPage — V2 real-time nurse call alerts page for Cloud 5.0 platform.
 *
 * 4 tabs: Emergency (real-time), Batteries (60s poll), Communication Lost (60s poll), Other (60s poll).
 * Sound notifications for new emergency alarms. Station Mode (fullscreen).
 * NotificationContext integration for real-time cloud emergency alerts.
 *
 * Key differences from 4.0 AlertsPage:
 *   - Uses `activeCloudAlarms` (not `activeAlarms`) from NotificationContext
 *   - Snake_case data schema (event_id, event_datetime_utc, event_category, etc.)
 *   - Batteries + Other tabs from `getdevicemaintenance`; Communication Lost from
 *     `sp_getDeviceOffline` (fetchLostDevices), the same source AlertsPage uses
 *   - No Clear button on maintenance alerts
 *   - No role-based group filtering on emergency alerts
 *   - Emergency table has Description, Accepted By/Time, Device Cleared columns
 *
 * Props: fullscreen — boolean for station mode (initial value)
 */

import { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import useSound from 'use-sound';
import NavigationV2 from '../../../components/v2/NavigationV2.jsx';
import { useNotifications } from '../../../contexts/NotificationContext.jsx';
import {
    getSession, getCampusId, getCampusList, updateSessionCampus,
    getUserId, getUsername, getSessionId, getCampusConfig, getActiveVendorIdList, setAlertsMobileView,
} from '../../../services/session.js';
import { sortFunc, setSoundsMute, iAmHigherAuthority } from '../../../libs.jsx';
import { fetchCloudMaintenance, acceptAlert, finishAlert, acknowledgeAlert, logAcknowledgeLinkTaken } from './api/cloudAlertsApi.js';
import { fetchLostDevices } from './api/alertsApi.js';
import { fetchReasonCodes, fetchHighPriorityEntities } from '../../settings/system-settings/api/cloudSettingsApi.js';

import CloudEmergencyTable from './components/CloudEmergencyTable.jsx';
import CloudMaintenanceTable from './components/CloudMaintenanceTable.jsx';
import LostDevicesTable from './components/LostDevicesTable.jsx';
import AlertSyncIndicator from './components/AlertSyncIndicator.jsx';
import StationModeInfoTip from './components/StationModeInfoTip.jsx';
import MobileModeInfoTip from './components/MobileModeInfoTip.jsx';
import FinishReasonModal from './components/FinishReasonModal.jsx';
import MobileAlertsView from '../../../components/mobile-alerts/MobileAlertsView.jsx';
import MobileReasonDrawer from '../../../components/mobile-alerts/MobileReasonDrawer.jsx';
import emergSound from '../../../sound/emergency_tone.mp3';

import './alerts-page.css';

const CloudAlertsPage = ({ fullscreen: fullscreenProp }) => {
    // ======================================================================
    // SESSION
    // ======================================================================
    // Held in state (not useMemo) so writes from other pages — e.g. saving
    // portal_afr in CloudSystemSettingsPage — propagate via the
    // `session:updated` event without requiring a full reload.
    const [session, setSessionState] = useState(() => getSession());
    useEffect(() => {
        const handler = () => setSessionState(getSession());
        window.addEventListener('session:updated', handler);
        return () => window.removeEventListener('session:updated', handler);
    }, []);
    const campusList = useMemo(() => getCampusList() || [], []);
    const [campusId, setCampusId] = useState(() => getCampusId());
    const campusConfig = useMemo(() => getCampusConfig(campusId), [campusId]);
    // SafelyYou (vendor 17) active for this campus — gates the Acknowledge action
    // (and its Actions column) independently of portal_afr.
    const ackIntegrationActive = useMemo(() => getActiveVendorIdList(campusId).includes(17), [campusId]);
    const isCloud = Boolean(campusConfig?.is_cloud);
    const portalAfr = Boolean(campusConfig?.portal_afr);
    // role_weight > 10 gets the "View Video" GET link (SafelyYou) alongside Acknowledge.
    const canViewVideo = useMemo(() => iAmHigherAuthority(10, session?.role_weight), [session]);

    // ======================================================================
    // NOTIFICATION CONTEXT (real-time cloud emergency alerts)
    // ======================================================================
    const {
        activeCloudAlarms, isConnected, connectionError, isDataStale,
        registerAlertViewer, unregisterAlertViewer, resubscribeToCurrentCampus,
    } = useNotifications();

    useEffect(() => {
        registerAlertViewer();
        return () => unregisterAlertViewer();
    }, [registerAlertViewer, unregisterAlertViewer]);

    // ======================================================================
    // STATE
    // ======================================================================
    const [activeTab, setActiveTab] = useState('emergency');
    const [fullscreen, setFullscreen] = useState(Boolean(fullscreenProp));
    // Seeded from the session so a refresh keeps the user in whatever mode they chose.
    const [mobileView, setMobileView] = useState(() => Boolean(getSession()?.alertsMobileView));
    const [lowBatData, setLowBatData] = useState([]);
    const [lostData, setLostData] = useState([]);
    const [otherData, setOtherData] = useState([]);

    // Sort state
    const [emergSort, setEmergSort] = useState({ col: 'event_datetime_utc', dir: 'asc' });
    const [maintSort, setMaintSort] = useState({ col: 'event_datetime_utc', dir: 'asc' });
    // Communication Lost uses its own source/shape (fetchLostDevices → LostDevicesTable).
    const [lostSort, setLostSort] = useState({ col: 'last_active', dir: 'asc' });

    // Sound
    const [emergencySound] = useSound(emergSound, { volume: 0.5 });
    const [soundOff, setSoundOff] = useState(session?.soundsOff || false);
    const alertKeyListRef = useRef([]);

    // High-priority event types (config) — Set of event_type_id (as strings) flagged
    // high priority for this campus. Loaded once per campus; used to pin + highlight.
    const [highPriorityTypeIds, setHighPriorityTypeIds] = useState(() => new Set());

    // Portal Accept/Finish
    const [reasonCodes, setReasonCodes] = useState([]);
    const [pendingEventId, setPendingEventId] = useState(null); // eventId with an in-flight accept/finish
    const [finishTarget, setFinishTarget] = useState(null);     // alarm row awaiting reason selection
    const [presetReasonId, setPresetReasonId] = useState(null); // reason to pre-select in the finish prompt (fall carve-out)

    // Toast
    const [toast, setToast] = useState(null);
    const toastTimerRef = useRef(null);
    const showToast = useCallback((type, message) => {
        clearTimeout(toastTimerRef.current);
        setToast({ type, message });
        toastTimerRef.current = setTimeout(() => setToast(null), 6000);
    }, []);
    useEffect(() => () => clearTimeout(toastTimerRef.current), []);

    // Toggle mobile mode and persist the choice to the session so it survives a refresh.
    const toggleMobileView = useCallback(() => {
        setMobileView((prev) => {
            const next = !prev;
            setAlertsMobileView(next);
            return next;
        });
    }, []);

    // ======================================================================
    // EMERGENCY ALERTS (from cloud context — no group filtering for 5.0)
    // ======================================================================
    const filteredEmergency = useMemo(() => {
        return [...(activeCloudAlarms || [])];
    }, [activeCloudAlarms]);

    // ======================================================================
    // SOUND — play for new alarm IDs
    // ======================================================================
    useEffect(() => {
        let playSound = false;
        const currentKeys = alertKeyListRef.current;
        filteredEmergency.forEach((alarm) => {
            if (!currentKeys.includes(alarm.event_id)) {
                if (!soundOff) playSound = true;
                currentKeys.push(alarm.event_id);
            }
        });
        alertKeyListRef.current = currentKeys;
        if (playSound) emergencySound();
    }, [filteredEmergency, soundOff, emergencySound]);

    // ======================================================================
    // SORTED DATA
    // ======================================================================
    // High-priority alarms always pin to the top; the active sort then orders
    // rows within the high-priority and normal groups independently.
    const isHighPriority = useCallback(
        (alarm) => highPriorityTypeIds.has((alarm?.event_type_id ?? '').toString()),
        [highPriorityTypeIds],
    );

    const sortedEmergency = useMemo(() => {
        const sorted = [...filteredEmergency];
        sorted.sort((a, b) => {
            const ap = isHighPriority(a) ? 1 : 0;
            const bp = isHighPriority(b) ? 1 : 0;
            if (ap !== bp) return bp - ap;
            return sortFunc(a, b, emergSort.col, emergSort.dir);
        });
        return sorted;
    }, [filteredEmergency, emergSort, isHighPriority]);

    const sortedLowBat = useMemo(() => {
        const sorted = [...lowBatData];
        sorted.sort((a, b) => sortFunc(a, b, maintSort.col, maintSort.dir));
        return sorted;
    }, [lowBatData, maintSort]);

    const sortedLost = useMemo(() => {
        const sorted = [...lostData];
        sorted.sort((a, b) => sortFunc(a, b, lostSort.col, lostSort.dir));
        return sorted;
    }, [lostData, lostSort]);

    const sortedOther = useMemo(() => {
        const sorted = [...otherData];
        sorted.sort((a, b) => sortFunc(a, b, maintSort.col, maintSort.dir));
        return sorted;
    }, [otherData, maintSort]);

    // ======================================================================
    // POLLING — Maintenance (60s) — all 3 non-emergency tabs from same endpoint
    // ======================================================================
    const loadMaintenance = useCallback(async () => {
        if (!campusId) return;
        try {
            const data = await fetchCloudMaintenance(campusId);
            const tmpBat = [];
            const tmpOther = [];
            (Array.isArray(data) ? data : []).forEach((item) => {
                const cond = (item.condition || '').toLowerCase();
                if (['low battery', 'missing battery'].includes(cond)) {
                    tmpBat.push(item);
                } else if (cond === 'communication lost') {
                    // Communication Lost now has its own source (loadLostDevices) — skip here.
                } else {
                    tmpOther.push(item);
                }
            });
            setLowBatData(tmpBat);
            setOtherData(tmpOther);
        } catch (err) {
            console.error('[CloudAlertsPage] loadMaintenance error:', err);
        }
    }, [campusId]);

    // Communication Lost comes from fetchLostDevices (sp_getDeviceOffline) — the same
    // source AlertsPage uses — not the shared getdevicemaintenance endpoint.
    const loadLostDevices = useCallback(async () => {
        if (!campusId) return;
        try {
            const data = await fetchLostDevices(campusId);
            setLostData(Array.isArray(data) ? data : []);
        } catch (err) {
            console.error('[CloudAlertsPage] loadLostDevices error:', err);
        }
    }, [campusId]);

    useEffect(() => { loadMaintenance(); }, [loadMaintenance]);
    useEffect(() => { loadLostDevices(); }, [loadLostDevices]);

    useEffect(() => {
        const maintTimer = setInterval(loadMaintenance, 180000);
        const lostTimer = setInterval(loadLostDevices, 180000);
        return () => { clearInterval(maintTimer); clearInterval(lostTimer); };
    }, [loadMaintenance, loadLostDevices]);

    // ======================================================================
    // HIGH-PRIORITY EVENT TYPES — config data, loaded once per campus.
    // Not real-time: it only changes when an admin edits High Priority settings,
    // so we fetch on mount + campus change (mirrors the reason-codes pattern) and
    // keep a Set of event_type_id for O(1) lookups while alarms stream in.
    // ======================================================================
    useEffect(() => {
        if (!campusId) {
            setHighPriorityTypeIds(new Set());
            return;
        }
        let cancelled = false;
        (async () => {
            try {
                const data = await fetchHighPriorityEntities(campusId);
                if (cancelled) return;
                const ids = (Array.isArray(data) ? data : [])
                    .map((r) => (r.event_type_id ?? '').toString())
                    .filter((v) => v !== '' && v !== 'null' && v !== 'undefined');
                setHighPriorityTypeIds(new Set(ids));
            } catch (err) {
                console.error('[CloudAlertsPage] fetchHighPriorityEntities error:', err);
                if (!cancelled) setHighPriorityTypeIds(new Set());
            }
        })();
        return () => { cancelled = true; };
    }, [campusId]);

    // ======================================================================
    // REASON CODES — loaded once per campus while portal_afr is enabled
    // ======================================================================
    useEffect(() => {
        if (!portalAfr || !campusId) {
            setReasonCodes([]);
            return;
        }
        let cancelled = false;
        (async () => {
            try {
                const data = await fetchReasonCodes(campusId);
                if (!cancelled) setReasonCodes(Array.isArray(data) ? data : []);
            } catch (err) {
                console.error('[CloudAlertsPage] fetchReasonCodes error:', err);
                if (!cancelled) setReasonCodes([]);
            }
        })();
        return () => { cancelled = true; };
    }, [portalAfr, campusId]);

    // ======================================================================
    // ACCEPT / FINISH HANDLERS
    // ======================================================================
    const handleAccept = useCallback(async (row) => {
        if (!row?.event_id || pendingEventId) return;
        setPendingEventId(row.event_id);
        try {
            const [ok, errMsg] = await acceptAlert({
                campusId: row.campus_id ?? campusId,
                eventId: row.event_id,
                userId: getUserId(),
                username: getUsername(),
                displayName: session?.display || getUsername(),
            });
            if (ok) {
                showToast('success', 'Alert accepted.');
                // AppSync subscription will repaint the row naturally.
            } else {
                showToast('error', errMsg || 'Failed to accept alert.');
            }
        } finally {
            setPendingEventId(null);
        }
    }, [pendingEventId, campusId, session, showToast]);

    // Acknowledge (POST to the SafelyYou acknowledge_link). Requires the alert to
    // already be accepted — the button in CloudEmergencyTable enforces that — so
    // this just fires the POST and toasts on success.
    const handleAcknowledge = useCallback(async (row) => {
        if (!row?.event_id || !row?.acknowledge_link || pendingEventId) return;
        setPendingEventId(row.event_id);
        try {
            const [ok, errMsg] = await acknowledgeAlert({
                campusId: row.campus_id ?? campusId,
                eventId: row.event_id,
                userId: getUserId(),
                clientId: getSessionId(),
                acknowledgeLink: row.acknowledge_link,
            });
            if (ok) {
                showToast('success', 'Alert has been acknowledged');
            } else {
                showToast('error', errMsg || 'Failed to acknowledge alert.');
            }
        } finally {
            setPendingEventId(null);
        }
    }, [pendingEventId, campusId, showToast]);

    // "View Video" opens the SafelyYou link (GET, new tab) directly. Separately fire
    // a silent background log of who took the link — no toast; console-log non-OK.
    const handleViewVideo = useCallback((row) => {
        if (!row?.event_id) return;
        logAcknowledgeLinkTaken({
            campusId: row.campus_id ?? campusId,
            eventId: row.event_id,
            userId: getUserId(),
            clientId: getSessionId(),
        }).then(([ok, errMsg]) => {
            if (!ok) console.error('[CloudAlertsPage] logAcknowledgeLinkTaken failed:', errMsg);
        });
    }, [campusId]);

    // Core finish — sends the alert to the clear/finish endpoint with a resolved
    // reason + resident. Used both by the reason prompt and the fall carve-out.
    const doFinish = useCallback(async (row, reasonId, residentId) => {
        if (!row?.event_id || !reasonId || pendingEventId) return;
        setPendingEventId(row.event_id);
        try {
            const [ok, errMsg] = await finishAlert({
                campusId: row.campus_id ?? campusId,
                eventId: row.event_id,
                reasonId,
                // single resident → implicit; multiple → user-selected; none → 0
                residentId: residentId ?? row.residents?.[0]?.resident_id ?? 0,
            });
            if (ok) {
                setFinishTarget(null);
                setPresetReasonId(null);
                showToast('success', 'Alert finished.');
            } else {
                showToast('error', errMsg || 'Failed to finish alert.');
            }
        } finally {
            setPendingEventId(null);
        }
    }, [pendingEventId, campusId, showToast]);

    const handleOpenFinish = useCallback((row) => {
        if (!row?.event_id || pendingEventId) return;

        // Carve-out: SafelyYou fall alerts (event_type_id 40) auto-apply a reason
        // whose description contains "fall" (case-insensitive) when one exists.
        // If the resident is unambiguous (0 or 1 in the area) we finish immediately
        // with no prompt; with multiple residents we still open the prompt so the
        // caregiver attributes the correct resident, but pre-select the fall reason.
        const isType40 = (row.event_type_id ?? '').toString() === '40';
        const fallReason = isType40
            ? (reasonCodes || []).find((rc) => (rc.reason_code_description || '').toLowerCase().includes('fall'))
            : null;

        if (fallReason) {
            const residents = row.residents || [];
            if (residents.length <= 1) {
                doFinish(row, fallReason.cloud_reason_code_id, residents[0]?.resident_id ?? 0);
                return;
            }
            setPresetReasonId(fallReason.cloud_reason_code_id);
            setFinishTarget(row);
            return;
        }

        setPresetReasonId(null);
        setFinishTarget(row);
    }, [pendingEventId, reasonCodes, doFinish]);

    const handleCancelFinish = useCallback(() => {
        setFinishTarget(null);
        setPresetReasonId(null);
    }, []);

    const handleSubmitFinish = useCallback((reasonId, residentId) => {
        if (!finishTarget) return;
        doFinish(finishTarget, reasonId, residentId);
    }, [finishTarget, doFinish]);

    // ======================================================================
    // HANDLERS
    // ======================================================================
    const handleCampusChange = useCallback((e) => {
        const val = parseInt(e.target.value, 10);
        const cfg = session?.campusData?.[val.toString()];
        // Cloud → on-premise redirect
        if (!cfg?.is_cloud && isCloud) {
            updateSessionCampus(val);
            window.location.href = '/alerts';
            return;
        }
        // On-premise → cloud redirect
        if (cfg?.is_cloud && !isCloud) {
            updateSessionCampus(val);
            window.location.href = '/emergency';
            return;
        }
        setCampusId(val);
        updateSessionCampus(val);
        resubscribeToCurrentCampus();
    }, [session, isCloud, resubscribeToCurrentCampus]);

    const handleSoundToggle = useCallback((e) => {
        const muted = e.target.checked;
        setSoundOff(muted);
        if (session) setSoundsMute(session, muted);
    }, [session]);

    const handleEmergSort = useCallback((col) => {
        setEmergSort((prev) => prev.col === col ? { col, dir: prev.dir === 'asc' ? 'desc' : 'asc' } : { col, dir: 'asc' });
    }, []);

    const handleMaintSort = useCallback((col) => {
        setMaintSort((prev) => prev.col === col ? { col, dir: prev.dir === 'asc' ? 'desc' : 'asc' } : { col, dir: 'asc' });
    }, []);

    const handleLostSort = useCallback((col) => {
        setLostSort((prev) => prev.col === col ? { col, dir: prev.dir === 'asc' ? 'desc' : 'asc' } : { col, dir: 'asc' });
    }, []);

    // ======================================================================
    // RENDER
    // ======================================================================
    return (
        <>
            {!fullscreen ? <NavigationV2 /> : null}
            <div className={fullscreen ? '' : 'nav2-main'}>
                <div className={`na-page${fullscreen ? ' na-page--fullscreen' : ''}`}>
                    {/* Toast */}
                    {toast ? (
                        <div className={`dp-toast dp-toast--${toast.type}`} role="status" aria-live="polite">
                            <span>{toast.message}</span>
                            <button onClick={() => setToast(null)} className="dp-toast__close">{'\u2715'}</button>
                        </div>
                    ) : null}

                    {/* Finish Reason Modal — table view only.
                        In mobile view, the drawer renders inside the mobile container so it
                        anchors to that area instead of the viewport. */}
                    {finishTarget && !mobileView ? (
                        <FinishReasonModal
                            row={finishTarget}
                            reasonCodes={reasonCodes}
                            saving={pendingEventId === finishTarget.event_id}
                            onSubmit={handleSubmitFinish}
                            onCancel={handleCancelFinish}
                            defaultReasonId={presetReasonId}
                        />
                    ) : null}

                    {/* Header */}
                    <div className="na-header">
                        <div className="na-header__left">
                            <h1 className="na-header__title">Emergency Alerts</h1>
                            <AlertSyncIndicator isConnected={isConnected} isDataStale={isDataStale} connectionError={connectionError} />
                        </div>
                        <div className="na-header__right">
                            <label className="na-sound-toggle">
                                <input type="checkbox" checked={soundOff} onChange={handleSoundToggle} />
                                <span>Mute Sound</span>
                            </label>
                            {!fullscreen ? (
                                <>
                                    {activeTab === 'emergency' ? (
                                        <MobileModeInfoTip>
                                            <button
                                                className={`na-mobile-toggle rp-btn rp-btn--${mobileView ? 'primary' : 'secondary'}${mobileView ? ' na-mobile-toggle--always' : ''}`}
                                                onClick={toggleMobileView}
                                            >
                                                <i className="fe fe-smartphone" style={{ marginRight: 4 }} />
                                                {mobileView ? 'Exit Mobile' : 'Mobile Mode'}
                                            </button>
                                        </MobileModeInfoTip>
                                    ) : null}
                                    {mobileView ? null : (
                                        <StationModeInfoTip>
                                            <button className="na-station-toggle rp-btn rp-btn--secondary" style={{ fontSize: 'var(--font-size-xs)', padding: '4px 12px' }} onClick={() => setFullscreen(true)}>
                                                <i className="fe fe-maximize" style={{ marginRight: 4 }} />Station Mode
                                            </button>
                                        </StationModeInfoTip>
                                    )}
                                    {mobileView ? null : (
                                        <div className="dp-campus-row">
                                            <label htmlFor="ca-campus" className="dp-campus-label">Campus:</label>
                                            {campusList.length > 1 ? (
                                                <select id="ca-campus" className="dp-campus-select" value={campusId} onChange={handleCampusChange}>
                                                    {campusList.map((c) => (<option key={c.campus_id} value={c.campus_id}>{c.campus_name}</option>))}
                                                </select>
                                            ) : (
                                                <span className="dp-campus-name">{campusConfig?.name || ''}</span>
                                            )}
                                        </div>
                                    )}
                                </>
                            ) : (
                                <button className="rp-btn rp-btn--secondary" style={{ fontSize: 'var(--font-size-xs)', padding: '4px 12px' }} onClick={() => setFullscreen(false)}>
                                    <i className="fe fe-minimize" style={{ marginRight: 4 }} />Exit Station Mode
                                </button>
                            )}
                        </div>
                    </div>

                    {/* Tab Bar — hidden in mobile mode so the user must exit to switch tabs. */}
                    {mobileView ? null : (
                    <div className="na-tab-bar">
                        <button className={`na-tab${activeTab === 'emergency' ? ' is-active' : ''}`} onClick={() => setActiveTab('emergency')}>
                            Emergency
                            {filteredEmergency.length > 0 ? <span className="na-badge na-badge--danger">{filteredEmergency.length}</span> : null}
                        </button>
                        <button className={`na-tab${activeTab === 'batteries' ? ' is-active' : ''}`} onClick={() => setActiveTab('batteries')}>
                            Batteries
                            {lowBatData.length > 0 ? <span className="na-badge na-badge--info">{lowBatData.length}</span> : null}
                        </button>
                        <button className={`na-tab${activeTab === 'lost' ? ' is-active' : ''}`} onClick={() => setActiveTab('lost')}>
                            Communication Lost
                            {lostData.length > 0 ? <span className="na-badge na-badge--info">{lostData.length}</span> : null}
                        </button>
                        <button className={`na-tab${activeTab === 'other' ? ' is-active' : ''}`} onClick={() => setActiveTab('other')}>
                            Other
                            {otherData.length > 0 ? <span className="na-badge na-badge--info">{otherData.length}</span> : null}
                        </button>
                    </div>
                    )}

                    {/* Content */}
                    <div className={`na-content${mobileView ? ' na-content--mobile' : ''}`}>
                        {activeTab === 'emergency' && mobileView ? (
                            <div className="na-mobile-wrap">
                                <MobileAlertsView
                                    alerts={filteredEmergency}
                                    sessionUserId={getUserId()}
                                    sessionUsername={getUsername()}
                                    onAccept={portalAfr ? handleAccept : undefined}
                                    onFinish={portalAfr ? handleOpenFinish : undefined}
                                    onAcknowledge={handleAcknowledge}
                                    onViewVideo={handleViewVideo}
                                    pendingEventId={pendingEventId}
                                    highPriorityTypeIds={highPriorityTypeIds}
                                    ackIntegrationActive={ackIntegrationActive}
                                    canViewVideo={canViewVideo}
                                />
                                {finishTarget ? (
                                    <MobileReasonDrawer
                                        open
                                        row={finishTarget}
                                        reasonCodes={reasonCodes}
                                        saving={pendingEventId === finishTarget.event_id}
                                        onSubmit={handleSubmitFinish}
                                        onCancel={handleCancelFinish}
                                        defaultReasonId={presetReasonId}
                                    />
                                ) : null}
                            </div>
                        ) : null}

                        {activeTab === 'emergency' && !mobileView ? (
                            <CloudEmergencyTable
                                data={sortedEmergency}
                                sortColumn={emergSort.col}
                                sortOrder={emergSort.dir}
                                onSort={handleEmergSort}
                                canInteract={portalAfr}
                                ackIntegrationActive={ackIntegrationActive}
                                canViewVideo={canViewVideo}
                                pendingEventId={pendingEventId}
                                onAccept={handleAccept}
                                onFinish={handleOpenFinish}
                                onAcknowledge={handleAcknowledge}
                                onViewVideo={handleViewVideo}
                                highPriorityTypeIds={highPriorityTypeIds}
                            />
                        ) : null}

                        {activeTab === 'batteries' ? (
                            <CloudMaintenanceTable data={sortedLowBat} sortColumn={maintSort.col} sortOrder={maintSort.dir} onSort={handleMaintSort} />
                        ) : null}

                        {activeTab === 'lost' ? (
                            <LostDevicesTable data={sortedLost} sortColumn={lostSort.col} sortOrder={lostSort.dir} onSort={handleLostSort} />
                        ) : null}

                        {activeTab === 'other' ? (
                            <CloudMaintenanceTable data={sortedOther} sortColumn={maintSort.col} sortOrder={maintSort.dir} onSort={handleMaintSort} />
                        ) : null}
                    </div>
                </div>
            </div>
        </>
    );
};

export default CloudAlertsPage;
